import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Chip,
  IconButton,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VerifiedIcon from "@mui/icons-material/Verified";
import Header from "../../components/Header";
import {
  useGetUserDetailsQuery,
  useGetUserMeetupsQuery,
  useGetUserWalletStatementQuery,
} from "../../state/api";

const mediaUrl = (path) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${axios.defaults.baseURL}/${path}`;
};

const meetupStatusColor = {
  pending: "#ed6c02",
  completed: "#2e7d32",
  accepted: "#0288d1",
  acceptedwithcounter: "#ed6c02",
  declined: "#d32f2f",
  cancelled: "#d32f2f",
  ongoing: "#0288d1",
  started: "#0288d1",
};

const statementStatusColor = {
  success: "#2e7d32",
  failed: "#d32f2f",
  pending: "#ed6c02",
};

const InfoField = ({ label, value, theme }) => (
  <Box mb="1rem">
    <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
      {label.toUpperCase()}
    </Typography>
    <Typography>{value ?? "N/A"}</Typography>
  </Box>
);

const UserDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();

  const { data: userResp, isLoading: userLoading } = useGetUserDetailsQuery(userId);
  const { data: meetupResp, isLoading: meetupLoading } = useGetUserMeetupsQuery(userId);
  const { data: statementResp, isLoading: statementLoading } = useGetUserWalletStatementQuery(userId);

  const profile = userResp && userResp.response && userResp.response[0];
  const meetups = (meetupResp && meetupResp.meetups) || [];
  const statements = (statementResp && statementResp.statementList) || [];

  const wallet = profile && profile.walletInfo && profile.walletInfo[0];
  const kyc = profile && profile.kycInfo && profile.kycInfo[0];
  const pkg = profile && profile.packageInfo && profile.packageInfo[0];

  const meetupColumns = [
    { field: "type", headerName: "Type", flex: 0.4, minWidth: 90 },
    {
      field: "withWhom",
      headerName: "With",
      flex: 0.6,
      minWidth: 130,
      valueGetter: (params) =>
        params.row.senderId === userId
          ? params.row.receiverName
          : params.row.senderName,
    },
    { field: "location", headerName: "Location", flex: 0.6, minWidth: 140 },
    { field: "date", headerName: "Date", flex: 0.4, minWidth: 110 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      minWidth: 110,
      renderCell: (params) => (
        <span
          style={{
            backgroundColor: meetupStatusColor[params.value] || "#555",
            color: "white",
            padding: "4px 10px",
            borderRadius: "4px",
            textTransform: "capitalize",
            fontSize: "0.8rem",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 0.4,
      minWidth: 110,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd"),
    },
  ];

  const statementColumns = [
    { field: "transType", headerName: "Type", flex: 0.4, minWidth: 100 },
    { field: "narration", headerName: "Narration", flex: 1, minWidth: 180 },
    {
      field: "debit",
      headerName: "Debit",
      flex: 0.4,
      minWidth: 90,
      renderCell: (params) => (params.value ? `-${params.value}` : "0"),
    },
    {
      field: "credit",
      headerName: "Credit",
      flex: 0.4,
      minWidth: 90,
      renderCell: (params) => (params.value ? `+${params.value}` : "0"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      minWidth: 100,
      renderCell: (params) => (
        <span
          style={{
            backgroundColor: statementStatusColor[params.value] || "#555",
            color: "white",
            padding: "4px 10px",
            borderRadius: "4px",
            textTransform: "capitalize",
            fontSize: "0.8rem",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 0.4,
      minWidth: 110,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd"),
    },
  ];

  if (userLoading) {
    return (
      <Box m="1.5rem 2.5rem" display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" alignItems="center" gap="1rem">
        <IconButton onClick={() => navigate("/user")}>
          <ArrowBackIcon />
        </IconButton>
        <Header title="USER DETAILS" />
      </Box>

      {!profile ? (
        <Typography mt="2rem">User not found.</Typography>
      ) : (
        <>
          <Box
            mt="1.5rem"
            p="1.5rem"
            borderRadius="0.75rem"
            sx={{ backgroundColor: theme.palette.background.alt }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={3} textAlign="center">
                <Avatar
                  src={mediaUrl(profile.profilePicture)}
                  sx={{ width: 96, height: 96, margin: "0 auto 1rem" }}
                >
                  {profile.fullname ? profile.fullname.charAt(0).toUpperCase() : "?"}
                </Avatar>
                <Typography variant="h4">{profile.fullname || "—"}</Typography>
                <Typography color={theme.palette.secondary[200]}>
                  @{profile.username || "—"}
                </Typography>
                <Box mt="0.5rem" display="flex" justifyContent="center" gap="0.5rem" flexWrap="wrap">
                  <Chip label={profile.status || "unknown"} size="small" />
                  {kyc && (
                    <Chip
                      icon={<VerifiedIcon />}
                      label={kyc.isVerified ? "KYC Verified" : "KYC Pending"}
                      size="small"
                      sx={{
                        backgroundColor: kyc.isVerified ? "#00c9a722" : "#ffda8522",
                        color: kyc.isVerified ? "#00c9a7" : "#ffda85",
                      }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <InfoField label="Email" value={profile.email} theme={theme} />
                <InfoField label="Mobile" value={profile.mobileNumber} theme={theme} />
                <InfoField label="Gender" value={profile.gender} theme={theme} />
                <InfoField label="Profession" value={profile.profession} theme={theme} />
                <InfoField label="Address" value={profile.addressLine} theme={theme} />
              </Grid>

              <Grid item xs={12} md={5}>
                <InfoField
                  label="Package"
                  value={pkg ? `${pkg.title} (NRs ${pkg.totalPrice || pkg.basePrice})` : "No package"}
                  theme={theme}
                />
                <InfoField
                  label="Credit Points"
                  value={wallet ? wallet.creditPoints : "N/A"}
                  theme={theme}
                />
                <InfoField
                  label="Reward Points"
                  value={wallet ? wallet.rewardPoints : "N/A"}
                  theme={theme}
                />
                <InfoField
                  label="Locked Credit Points"
                  value={wallet ? wallet.lockedCreditPoints : "N/A"}
                  theme={theme}
                />
                <InfoField label="User Rating" value={profile.userRating} theme={theme} />
              </Grid>
            </Grid>
          </Box>

          <Box mt="2rem">
            <Header title="MEETING HISTORY" />
            <Box
              mt="1rem"
              height="50vh"
              sx={{
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-cell": { borderBottom: "none" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: theme.palette.primary.success,
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderTop: "none",
                },
              }}
            >
              <DataGrid
                loading={meetupLoading}
                getRowId={(row) => row._id}
                rows={meetups}
                columns={meetupColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </Box>
          </Box>

          <Box mt="2rem" mb="2rem">
            <Header title="WALLET STATEMENT" />
            <Box
              mt="1rem"
              height="50vh"
              sx={{
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-cell": { borderBottom: "none" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: theme.palette.primary.success,
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderTop: "none",
                },
              }}
            >
              <DataGrid
                loading={statementLoading}
                getRowId={(row) => row._id}
                rows={statements}
                columns={statementColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UserDetails;
